import { useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppointmentData, ClientData, FeatureEnum, InvoiceData, Invoice, InvoiceItem, InvoiceItemType } from '../models'
import { getClientAppointmentsAsync } from '../ajax/requests'
import { getExtrasSettings, getGeneralSettings, getInvoiceSettings } from '../store/settings/selectors'
import { TaxDataHook } from './useTaxData'
import { useTranslations } from '../store/translations/hooks'
import { TK } from '../store/translations/translationKeys'
import { alertError } from '../store/alerts/actions'
import { hasFeature } from '../store/session/selectors'
import { getSelectedBranch } from '../store/general/selectors'
import {
    resolveTaxDetails,
    resolveEffectiveLegalInfo,
    applyAppointmentPlaceholders,
    mapExtrasToInvoiceItems,
    mergeInvoiceItems,
    areInvoiceItemsEqual,
    buildAppointmentNotes,
    sumNegativeExtras,
    DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE,
} from '../shared/appointmint/invoice'
import {
    canSelectInvoiceAppointmentBranch,
    getSelectedInvoiceBranchKey,
} from '../shared/appointmint/invoiceBranches'

export interface UseInvoiceAppointmentsProps {
    value: InvoiceData | Invoice
    onChange?: (value: InvoiceData) => void
    appointmentIds?: string[]
    onAppointmentIdsChange?: (appointmentIds: string[]) => void
    canSelectAppointments: boolean
    taxHook: TaxDataHook
    client?: ClientData
}

export const useInvoiceAppointments = ({
    value,
    onChange,
    appointmentIds,
    onAppointmentIdsChange,
    canSelectAppointments,
    taxHook,
    client,
}: UseInvoiceAppointmentsProps) => {
    const dispatch = useDispatch()
    const t = useTranslations()
    const generalSettings = useSelector(getGeneralSettings)
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches))
    const selectedBranch = useSelector(getSelectedBranch)
    const extrasSettings = useSelector(getExtrasSettings)
    const invoiceSettings = useSelector(getInvoiceSettings)
    const shouldAppendAppointmentNotes = !!invoiceSettings?.addAppointmentPackDetailsAutomaticallyOnInvoice
    const appointmentPackDetailsTemplate = invoiceSettings?.appointmentPackDetailsTemplate || DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE

    const [appointmentsModalOpen, setAppointmentsModalOpen] = useState(false)
    const [appointmentsLoading, setAppointmentsLoading] = useState(false)
    const [clientAppointments, setClientAppointments] = useState<AppointmentData[]>([])
    const [appointmentsClientId, setAppointmentsClientId] = useState<string | null>(null)
    const [observationsBase, setObservationsBase] = useState(value.observations ?? '')
    const [appendedNotes, setAppendedNotes] = useState('')

    const selectedAppointmentIds = appointmentIds ?? []

    /**
     * Build invoice items from appointments
     */
    const buildAppointmentItems = useCallback((appointmentsToUse: AppointmentData[]): InvoiceItem[] => {
        const extras = extrasSettings ?? [];
        const itemsFromAppointments = appointmentsToUse.flatMap((appointment) => {
            const companyLegalInfo = resolveEffectiveLegalInfo(invoiceSettings, appointment.branchId);
            const invoiceItemDetails = generalSettings?.services?.[appointment.type]?.invoiceItemDetails;
            const { taxId, taxExemption, taxExemptionLaw } = resolveTaxDetails(
                invoiceItemDetails,
                companyLegalInfo,
            );

            const services = generalSettings?.services;
            const serviceDisplayName = services?.[appointment.type]?.name || appointment.type;
            const rawTitle = invoiceItemDetails?.customTitle || serviceDisplayName;
            const rawDescription = invoiceItemDetails?.customDescription || serviceDisplayName;

            const serviceItem: InvoiceItem = {
                code: applyAppointmentPlaceholders(rawTitle, appointment, services, client),
                description: applyAppointmentPlaceholders(rawDescription, appointment, services, client),
                quantity: 1,
                unitPrice: appointment.price,
                taxId,
                typeId: invoiceItemDetails?.type || InvoiceItemType.S,
                taxExemption,
                taxExemptionLaw,
            };

            return [
                serviceItem,
                ...mapExtrasToInvoiceItems(
                    appointment.expenses ?? [],
                    extras,
                    companyLegalInfo,
                    appointment,
                    services,
                    client,
                ),
            ];
        });

        return mergeInvoiceItems(itemsFromAppointments);
    }, [generalSettings?.services, extrasSettings, invoiceSettings, client]);

    const mergeObservations = useCallback((base: string, notes: string) => {
        return [base, notes].filter(part => part.length > 0).join('\n');
    }, []);

    /**
     * Update invoice items and observations from selected appointments
     */
    const updateInvoiceItemsFromAppointments = useCallback((nextAppointmentIds: string[], appointmentsToUse: AppointmentData[] = clientAppointments) => {
        if (!onChange) return;

        if (nextAppointmentIds.length === 0) {
            const observations = observationsBase.length > 0 ? observationsBase : undefined;
            setAppendedNotes('');
            onChange({
                ...value,
                branchId: useBranches ? selectedBranch : undefined,
                items: [],
                observations,
                globalDiscountAmount: undefined,
            })
            return;
        }

        if (!canSelectAppointments) return;

        const selectedAppointments = appointmentsToUse.filter(a => nextAppointmentIds.includes(a.id));
        if (!selectedAppointments.length) return;

        const nextAppointmentNotes = shouldAppendAppointmentNotes
            ? buildAppointmentNotes(selectedAppointments, appointmentPackDetailsTemplate, generalSettings?.services, client)
            : '';
        const nextObservations = mergeObservations(observationsBase, nextAppointmentNotes);
        setAppendedNotes(nextAppointmentNotes);
        const nextItems = buildAppointmentItems(selectedAppointments);
        const itemsChanged = !areInvoiceItemsEqual(value.items ?? [], nextItems);
        const observationsChanged = (value.observations ?? '') !== nextObservations;
        const nextGlobalDiscountAmount = (() => {
            const total = sumNegativeExtras(
                selectedAppointments,
                extrasSettings ?? [],
                invoiceSettings,
                taxHook.taxRatesMap
            );
            return total > 0 ? total : undefined;
        })();
        const discountChanged = value.globalDiscountAmount !== nextGlobalDiscountAmount;

        if (itemsChanged || observationsChanged || discountChanged) {
            onChange({
                ...value,
                branchId: selectedAppointments[0]?.branchId ?? undefined,
                items: nextItems,
                observations: nextObservations.length > 0 ? nextObservations : undefined,
                globalDiscountAmount: nextGlobalDiscountAmount,
            })
        }
    }, [onChange, canSelectAppointments, value, clientAppointments, buildAppointmentItems, mergeObservations, observationsBase, useBranches, selectedBranch, extrasSettings, invoiceSettings, shouldAppendAppointmentNotes, appointmentPackDetailsTemplate, generalSettings?.services, taxHook.taxRatesMap]);

    /**
     * Open appointments modal and fetch appointments if needed
     */
    const handleOpenAppointmentsModal = useCallback(() => {
        if (!value.clientId) return
        setAppointmentsModalOpen(true)
        if (appointmentsClientId === value.clientId && clientAppointments.length > 0) return

        setAppointmentsLoading(true)
        getClientAppointmentsAsync(value.clientId)
            .then((appointments) => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(23, 59, 59, 999); // End of tomorrow

                // Filter out:
                // 1. Future appointments (date is after tomorrow)
                // 2. Appointments that already have ANY invoices to prevent double-invoicing
                const availableAppointments = appointments.filter(appt => {
                    const apptDate = new Date(appt.date);
                    const isFuture = apptDate > tomorrow;
                    const hasInvoices = appt.invoices && appt.invoices.length > 0;
                    return !isFuture && !hasInvoices;
                });
                setClientAppointments(availableAppointments)
                setAppointmentsClientId(value.clientId ?? null)
            })
            .catch((error) => {
                console.error('Error fetching client appointments:', error)
                setClientAppointments([])
            })
            .finally(() => {
                setAppointmentsLoading(false)
            })
    }, [value.clientId, appointmentsClientId, clientAppointments.length])

    /**
     * Toggle appointment selection
     */
    const handleAppointmentToggle = useCallback((appointmentId: string) => {
        const appointmentToToggle = clientAppointments.find(appointment => appointment.id === appointmentId)
        const selectedBranchKey = getSelectedInvoiceBranchKey(selectedAppointmentIds, clientAppointments)

        if (!selectedAppointmentIds.includes(appointmentId)
            && !canSelectInvoiceAppointmentBranch(selectedBranchKey, appointmentToToggle?.branchId))
        {
            dispatch(alertError(t(TK.appointmentsMustBelongToSameBranch)))
            return
        }

        const nextIds = selectedAppointmentIds.includes(appointmentId)
            ? selectedAppointmentIds.filter(id => id !== appointmentId)
            : [...selectedAppointmentIds, appointmentId]
        onAppointmentIdsChange && onAppointmentIdsChange(nextIds)
        updateInvoiceItemsFromAppointments(nextIds)
    }, [clientAppointments, dispatch, onAppointmentIdsChange, selectedAppointmentIds, t, updateInvoiceItemsFromAppointments])

    /**
     * Clear appointments when they can no longer be selected
     */
    useEffect(() => {
        if (!canSelectAppointments) {
            setClientAppointments([])
            setAppointmentsClientId(null)
            return
        }

        if (appointmentsClientId && appointmentsClientId !== value.clientId) {
            setClientAppointments([])
            setAppointmentsClientId(null)
        }
    }, [appointmentsClientId, canSelectAppointments, value.clientId])

    /**
     * Update invoice items when appointments are loaded for the first time
     * Do NOT re-run when updateInvoiceItemsFromAppointments changes to prevent overriding manual edits
     */
    useEffect(() => {
        if (selectedAppointmentIds.length > 0 && clientAppointments.length > 0) {
            updateInvoiceItemsFromAppointments(selectedAppointmentIds, clientAppointments)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientAppointments, selectedAppointmentIds])

    useEffect(() => {
        if (selectedAppointmentIds.length > 0 && clientAppointments.length > 0) {
            updateInvoiceItemsFromAppointments(selectedAppointmentIds, clientAppointments)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldAppendAppointmentNotes, appointmentPackDetailsTemplate])

    /**
     * Reset observations base when appropriate
     */
    useEffect(() => {
        const current = value.observations ?? '';
        if (appendedNotes.length > 0) {
            const suffix = `\n${appendedNotes}`;
            if (current.endsWith(suffix)) {
                const nextBase = current.slice(0, -suffix.length);
                if (nextBase !== observationsBase) {
                    setObservationsBase(nextBase);
                }
                return;
            }
        }

        if (current !== observationsBase) {
            setObservationsBase(current);
            if (appendedNotes.length > 0) {
                setAppendedNotes('');
            }
        }
    }, [value.observations, appendedNotes, observationsBase])

    return {
        appointmentsModalOpen,
        setAppointmentsModalOpen,
        appointmentsLoading,
        clientAppointments,
        selectedAppointmentIds,
        observationsBase,
        setObservationsBase,
        appendedNotes,
        setAppendedNotes,
        handleOpenAppointmentsModal,
        handleAppointmentToggle,
        updateInvoiceItemsFromAppointments,
    }
}
